define("Widgets/ScrollContent2", [`Controller`, `module`], (Super, Self) => {
	return Super.inherit({
		namespace: Self.id,
		defaults: {
			loadStylesheet: true,
			arrowsForDesktop: true,
			arrowMode: `edges`, // edges, below
			mode: `standard`, // standard, highlight - highlight mode makes images larger and centers them
			minScale: 1, // minimum scale for highlight mode
			maxScale: 1.12, // maximum scale for highlight mode
			alignWithElement: null, // element to align with in highlight mode
			startIndex: 0, // index of the item to start scrolling at

			/**
			 * callbacks: {
			 * 	 onIndexChange: function({index, displayLeftTrigger, displayRightTrigger}) { ... },
			 * }
			 */
		},
		requires: [`App`],
		constructor(setup) {
			const { data, elements } = this;
			const { alignWithElement, arrowsForDesktop, arrowMode, minScale, maxScale, mode, startIndex } = setup;

			this.addClass(`arrow-style-${arrowMode}`);

			this.assign(data, {
				alignWithElement,
				arrowsForDesktop,
				minScale,
				maxScale,
				mode,
				startIndex,
			});

			elements.list = this.get(`ul`);
			elements.items = this.select(`ul > li`);

			if (arrowsForDesktop) {
				this.createArrows();
			}

			if (arrowsForDesktop || this.isFunction(setup.callbacks.onIndexChange)) {
				this.updateScrollArrows();
			}

			if (mode === `highlight`) {
				this.alignHighlightedElement();
				this.updateItemScale({ init: true });
			} else if (startIndex > 0 && this.isArray(elements.items, startIndex + 1)) {
				this.scrollByDistanceToIndex({ startIndex, behavior: `instant` });
			}
		},
		api: {
			createArrows: {
				value() {
					const arrowContainer = document.createElement(`div`);
					const leftArrowElement = this.createLeftArrow();
					const rightArrowElement = this.createRightArrow();

					this.addClass(`arrow-container`, arrowContainer);
					this.container.appendChild(arrowContainer);

					arrowContainer.appendChild(leftArrowElement);
					arrowContainer.appendChild(rightArrowElement);
				},
			},

			createLeftArrow: {
				value() {
					const leftArrowElement = document.createElement(`a`);

					leftArrowElement.setAttribute(`href`, `#`);
					leftArrowElement.setAttribute(`action`, `scroll-left`);
					this.addClass(`hide-mobile`, leftArrowElement);
					leftArrowElement.setAttribute(`aria-label`, `scroll left`);
					leftArrowElement.innerHTML = this.Templates.getIcon(`arrow-left`);

					return leftArrowElement;
				},
			},

			createRightArrow: {
				value() {
					const rightArrowElement = document.createElement(`a`);

					rightArrowElement.setAttribute(`href`, `#`);
					rightArrowElement.setAttribute(`action`, `scroll-right`);
					this.addClass(`hide-mobile`, rightArrowElement);
					rightArrowElement.setAttribute(`aria-label`, `scroll right`);
					rightArrowElement.innerHTML = this.Templates.getIcon(`arrow-right`);

					return rightArrowElement;
				},
			},

			// Align the highlighted element with the alignWithElement
			alignHighlightedElement: {
				value() {
					const { data, DOM, System } = this;
					const { alignWithElement, maxScale, mode } = data;
					const system = System.get();
					const { isMobile } = system;

					if (mode === `highlight` && !isMobile && alignWithElement != null) {
						const listElement = this.get(`ul.gallery`);
						const itemAnchorElements = this.select(`li a`, listElement);
						const alignWithPadding = DOM.getDimensions(alignWithElement).paddingLeft;

						const newWidth = (window.innerWidth - alignWithPadding * 2) / maxScale;
						const newPaddingCalculated = `calc(${(newWidth * (maxScale - 1)) / 2 + alignWithPadding}px - 3vw)`; // 3vw is the desktop margin
						const actualnewPadding = `max(${newPaddingCalculated}, ${alignWithPadding}px)`;

						this.each(itemAnchorElements, (item) => {
							item.style.width = `${newWidth}px`;
						});

						listElement.style.paddingLeft = actualnewPadding;
						listElement.style.paddingRight = actualnewPadding;
					}
				},
			},

			checkForUpdates: {
				public: true,
				value(context, options = { skipCallbacks: false }) {
					const { elements } = this;
					elements.items = this.select(`ul > li`);
					this.updateScrollArrows(options);
				},
			},

			updateScrollArrows: {
				debounce: 200,
				value(options = { skipCallbacks: false }) {
					if (this.isArray(this.elements.items, 1)) {
						const { DOM, callbacks, data, elements } = this;
						const { arrowsForDesktop, mode } = data;
						const { items, list } = elements;
						const leftArrowElement = this.get(`[action="scroll-left"]`);
						const rightArrowElement = this.get(`[action="scroll-right"]`);
						const disabledClass = `disabled`;
						const firstElement = items[0];
						const lastElement = items[items.length - 1];
						const epsilon = 1;
						let shouldShowLeftArrow = false;
						let showRightArrow = false;

						DOM.deferUICalculations(list, () => {
							const listDimensions = DOM.getDimensions(list);

							if (mode === `highlight`) {
								shouldShowLeftArrow = DOM.getDimensions(firstElement).left < listDimensions.left;
							} else {
								shouldShowLeftArrow =
									listDimensions.left + listDimensions.paddingLeft - DOM.getDimensions(firstElement).left > epsilon;
							}

							if (
								DOM.getDimensions(lastElement).right - (listDimensions.right - listDimensions.paddingRight) >
								epsilon
							) {
								showRightArrow = true;
							}

							if (arrowsForDesktop) {
								if (shouldShowLeftArrow || showRightArrow) {
									this.show({ target: leftArrowElement });
									this.show({ target: rightArrowElement });
								} else {
									this.hide({ target: leftArrowElement });
									this.hide({ target: rightArrowElement });
								}

								if (shouldShowLeftArrow) {
									this.removeClass(disabledClass, leftArrowElement);
								} else {
									this.addClass(disabledClass, leftArrowElement);
								}

								if (showRightArrow) {
									this.removeClass(disabledClass, rightArrowElement);
								} else {
									this.addClass(disabledClass, rightArrowElement);
								}
							}

							let closestElementIndex = 0;
							let closestElementDistance = Infinity;
							// find centered most element index
							this.each(items, (item, index) => {
								const itemDimensions = DOM.getDimensions(item);
								const itemCenter = itemDimensions.left + itemDimensions.width / 2;

								// get device center x
								const deviceCenter = window.innerWidth / 2;
								const distance = Math.abs(itemCenter - deviceCenter);

								if (distance < closestElementDistance) {
									closestElementIndex = index;
									closestElementDistance = distance;
								}
							});

							if (!options.skipCallbacks && this.isFunction(callbacks.onIndexChange)) {
								callbacks.onIndexChange({
									index: closestElementIndex,
									displayLeftTrigger: shouldShowLeftArrow,
									displayRightTrigger: showRightArrow,
								});
							}
						});
					}
				},
			},

			/**
			 * Update the scale of the items based on their distance from the center of the screen
			 * @param {boolean} init - Whether this is the first time the function is being called. Used to guarantee the scale of the first item
			 */
			updateItemScale: {
				value({ init = false } = {}) {
					const { data } = this;
					const { minScale, maxScale, mode } = data;

					if (mode === `highlight`) {
						const { elements, DOM, Utils } = this;
						const { items } = elements;

						if (items.length > 1) {
							this.each(items, (item, index) => {
								const itemDimensions = DOM.getDimensions(item);
								const itemCenter = itemDimensions.left + (itemDimensions.width / 2) * maxScale;
								let newScale = Utils.lerp(
									maxScale,
									minScale,
									Math.abs(itemCenter - window.innerWidth / 2) / (window.innerWidth / 2)
								);

								if (init && index === 0) {
									newScale = maxScale;
								}

								item.style.transform = `scale(${newScale})`;
							});
						} else {
							this.addClass(`single-item`);
						}
					}
				},
			},

			scrollLeft: {
				public: true,
				value() {
					const { DOM, callbacks, data, elements } = this;
					const { mode } = data;
					const { items, list } = elements;
					const listDimensions = DOM.getDimensions(list);
					const epsilon = 1;
					let cutoffElement = null;
					let cutoffElementDimensions = null;
					let isPotentialCutoffElement = null;
					let cutoffElementIndex = -1;
					let getScrollAmount = null;

					if (mode === `highlight`) {
						isPotentialCutoffElement = (item) => {
							return DOM.getDimensions(item).left < listDimensions.left;
						};

						getScrollAmount = (cutoffElementDimensions) => {
							return cutoffElementDimensions.left + cutoffElementDimensions.width / 2 - window.innerWidth / 2;
						};
					} else {
						isPotentialCutoffElement = (item) => {
							return DOM.getDimensions(item).left + epsilon < listDimensions.left + listDimensions.paddingLeft;
						};

						getScrollAmount = (cutoffElementDimensions) => {
							return cutoffElementDimensions.right - listDimensions.right + listDimensions.paddingRight;
						};
					}

					this.each(items, (item, index) => {
						if (this.isFunction(isPotentialCutoffElement)) {
							if (isPotentialCutoffElement(item)) {
								cutoffElement = item;
								cutoffElementIndex = index;
								cutoffElementDimensions = DOM.getDimensions(cutoffElement);
							}
						}
					});

					if (cutoffElement != null) {
						if (this.isFunction(getScrollAmount)) {
							list.scrollBy(getScrollAmount(cutoffElementDimensions), 0);
						}

						if (this.isFunction(callbacks.onItemSelected) && cutoffElementIndex >= 0) {
							callbacks.onItemSelected(cutoffElementIndex);
						}
					}
				},
			},
			scrollRight: {
				public: true,
				value() {
					const { DOM, data, elements } = this;
					const { mode } = data;
					const { items, list } = elements;
					const listDimensions = DOM.getDimensions(list);
					let cutoffElement = null;
					let cutoffElementDimensions = null;
					const epsilon = 1;

					this.each(items, (item) => {
						if (
							listDimensions.right - listDimensions.paddingRight < DOM.getDimensions(item).right - epsilon &&
							cutoffElement == null
						) {
							cutoffElement = item;
							cutoffElementDimensions = DOM.getDimensions(cutoffElement);
						}
					});

					if (cutoffElement != null) {
						if (mode === `highlight`) {
							list.scrollBy(
								cutoffElementDimensions.left + cutoffElementDimensions.width / 2 - window.innerWidth / 2,
								0
							);
						} else {
							list.scrollBy(
								DOM.getDimensions(cutoffElement).left - listDimensions.left - listDimensions.paddingLeft,
								0
							);
						}
					}
				},
			},

			scrollByDistanceToIndex: {
				public: true,
				// Not yet implemented for highlight mode
				value(context, { index, behavior = `smooth` }) {
					const { DOM, elements } = this;
					const { items, list } = elements;
					const listDimensions = DOM.getDimensions(list);

					list.scrollBy({
						left: DOM.getDimensions(items[index]).left - listDimensions.left - listDimensions.paddingLeft,
						top: 0,
						behavior,
					});
				},
			},

			findNextElement: {
				public: true,
				value() {
					this.scrollRight();
				},
			},
		},

		listeners: [
			{
				event: `click`,
				filter: `[action]`,
				handler(evt) {
					const { currentTarget } = evt;
					const action = currentTarget.getAttribute(`action`);

					switch (action) {
						case `scroll-left`:
							this.scrollLeft();
							break;
						case `scroll-right`:
							this.scrollRight();
							break;
						default:
							break;
					}
				},
			},
			{
				event: `scroll`,
				target: `ul`,
				handler() {
					this.updateItemScale();
					this.updateScrollArrows();
				},
			},
			{
				event: `resize`,
				target: window,
				debounce: 200, // don't fire too often
				handler() {
					this.alignHighlightedElement();
					this.updateScrollArrows();
				},
			},
		],
	});
});

//# sourceMappingURL=ScrollContent2.js.map